/**
 * AG-UI over server-sent events, as emitted by the orchestrator at GET /v1/runs/{id}/events.
 *
 * We parse SSE ourselves (fetch + ReadableStream) instead of EventSource because EventSource
 * cannot send the bearer token, and instead of `@ag-ui/client` because that client POSTs a
 * RunAgentInput to an agent endpoint while our runs are started separately (POST /v1/runs) and
 * the stream is a resumable GET (`Last-Event-ID`). See ADR-0037.
 */
import type { Http } from "./http";
import { explainReason } from "./reasons";
import { richReplyOf, type RichReply } from "./richReply";
import type { Citation } from "./types";
import { viewBlockOf } from "./viewData";
import type { ViewBlock } from "./views";

export const TERMINAL_EVENTS = ["RUN_FINISHED", "RUN_ERROR", "RUN_CANCELLED"] as const;

export type AgUiEventType =
  | "RUN_STARTED"
  | "RUN_FINISHED"
  | "RUN_ERROR"
  | "RUN_CANCELLED"
  | "STEP_STARTED"
  | "STEP_FINISHED"
  | "TEXT_MESSAGE_START"
  | "TEXT_MESSAGE_CONTENT"
  | "TEXT_MESSAGE_END"
  | "TOOL_CALL_START"
  | "TOOL_CALL_ARGS"
  | "TOOL_CALL_END"
  | "CUSTOM";

/** One AG-UI event. Extra fields are kept: the orchestrator adds `tool`, `action`, `node`. */
export interface AgUiEvent {
  type: AgUiEventType | (string & {});
  runId?: string;
  threadId?: string;
  [key: string]: unknown;
}

export interface SseMessage {
  id: string | null;
  event: string | null;
  data: string;
}

/** Incremental SSE parser (WHATWG event-stream format). */
export async function* parseSse(stream: ReadableStream<Uint8Array>): AsyncGenerator<SseMessage> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let id: string | null = null;
  let event: string | null = null;
  let data: string[] = [];
  const flush = (): SseMessage | null => {
    if (data.length === 0) {
      event = null;
      return null;
    }
    const msg = { id, event, data: data.join("\n") };
    event = null;
    data = [];
    return msg;
  };
  try {
    for (;;) {
      const { value, done } = await reader.read();
      buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buffer.search(/\r\n|\r|\n/)) >= 0) {
        const line = buffer.slice(0, nl);
        buffer = buffer.slice(nl + (buffer.startsWith("\r\n", nl) ? 2 : 1));
        if (line === "") {
          const msg = flush();
          if (msg) yield msg;
          continue;
        }
        if (line.startsWith(":")) continue; // comment / keep-alive
        const colon = line.indexOf(":");
        const field = colon < 0 ? line : line.slice(0, colon);
        let val = colon < 0 ? "" : line.slice(colon + 1);
        if (val.startsWith(" ")) val = val.slice(1);
        if (field === "data") data.push(val);
        else if (field === "event") event = val;
        else if (field === "id" && !val.includes("\0")) id = val;
      }
      // Per the event-stream spec an event cut off before its blank line is not dispatched.
      if (done) return;
    }
  } finally {
    reader.releaseLock();
  }
}

export interface StreamOptions {
  signal?: AbortSignal;
  lastEventId?: string | null;
  /** Reconnect attempts after a dropped connection (resumes from the last event id). */
  maxRetries?: number;
  retryDelayMs?: number;
  /** Also stream the runs of team members, nested (ADR-0093 `members=true`). */
  members?: boolean;
}

/** Streams a run's AG-UI events until a terminal event, resuming on dropped connections. */
export async function* streamRunEvents(
  http: Http,
  runId: string,
  opts: StreamOptions = {},
): AsyncGenerator<{ id: string | null; event: AgUiEvent }> {
  let lastId = opts.lastEventId ?? null;
  let retries = 0;
  const max = opts.maxRetries ?? 5;
  for (;;) {
    let res: Response;
    try {
      res = await http.raw("orchestrator", `/v1/runs/${encodeURIComponent(runId)}/events${opts.members ? "?members=true" : ""}`, {
        headers: { Accept: "text/event-stream", ...(lastId ? { "Last-Event-ID": lastId } : {}) },
        signal: opts.signal,
      });
    } catch (err) {
      if (opts.signal?.aborted || retries >= max || (err as { status?: number }).status) throw err;
      retries += 1;
      await delay(opts.retryDelayMs ?? 1000 * retries);
      continue;
    }
    if (!res.body) throw new Error("event stream has no body");
    let terminal = false;
    try {
      for await (const msg of parseSse(res.body)) {
        let event: AgUiEvent;
        try {
          event = JSON.parse(msg.data) as AgUiEvent;
        } catch {
          continue; // ignore malformed frames
        }
        if (!event.type && msg.event) event.type = msg.event;
        if (msg.id) lastId = msg.id;
        retries = 0;
        yield { id: msg.id, event };
        if ((TERMINAL_EVENTS as readonly string[]).includes(event.type)) {
          terminal = true;
          return;
        }
      }
    } catch (err) {
      if (opts.signal?.aborted) throw err;
    }
    if (terminal || opts.signal?.aborted) return;
    if (retries >= max) throw new Error("event stream closed before the run finished");
    retries += 1;
    await delay(opts.retryDelayMs ?? 1000 * retries);
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

// ------------------------------------------------------------------------------ reducer

export type RunStatus = "idle" | "running" | "awaiting_approval" | "finished" | "error" | "cancelled";

/** A small text download offered with an answer (e.g. the CSV of a table, ADR-0068). */
export interface AnswerFile {
  name: string;
  media_type: string;
  text: string;
}
export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  streaming: boolean;
  /** "markdown" for answers built by a tool step (tables); plain text otherwise. */
  format?: "markdown" | "text";
  files?: AnswerFile[];
  /** A short version to read aloud (answers with tables or long text), when the agent gave one. */
  spoken?: string;
  /**
   * Text of an intermediate step that another step uses (a classifier's code, a draft): kept for
   * the activity panel, never shown as the agent's answer (TEXT_MESSAGE_START `internal`).
   */
  internal?: boolean;
  /** The spec step that wrote it (TEXT_MESSAGE_START `step`). */
  step?: string;
  /**
   * A line from the platform, not the agent (ADR-0084): `correction` = "Got it — switching to
   * your correction" (the text comes from i18n where it is shown).
   */
  notice?: "correction" | "helping";
  /** A team member who answered or now helps (handoff teams, ADR-0093): its name and label. */
  member?: string;
  memberLabel?: { en: string; ar: string } | null;
  /** `notice: "helping"`: why the conversation went to this member. */
  helpingReason?: string | null;
  /** An answer that was still being written when the person corrected the request. */
  cutOff?: boolean;
  /** The person talked over this answer while it was read aloud. */
  readCutOff?: boolean;
  /** The plain-text version of a rich reply (TEXT_MESSAGE_START `rich_fallback`, ADR-0088). */
  richFallback?: string;
  /** The rich reply drawn in this message's place (CUSTOM `rich_reply`, ADR-0088). */
  rich?: RichReply;
  /** Results shown as tables, maps, networks, … under the answer (CUSTOM `answer_views`, ADR-0116/0117). */
  views?: ViewBlock[];
  /** The plain-text version of an answer's displays (TEXT_MESSAGE_START `view_fallback`): hidden where they are drawn. */
  viewFallback?: boolean;
}
/** What the request a correction replaced had already done (CUSTOM `correction_applied`). */
export interface CorrectionView {
  /** stopped | finished | failed | unknown */
  status: string;
  steps: { en: string[]; ar: string[] };
  /** Actions that changed something: reported, not undone. */
  changed: { node: string | null; label: { en: string; ar: string } | null }[];
}
/** The kinds of action an answer can claim (ADR-0100): opened a case, sent, paid, granted, booked. */
export type ClaimKind = "case" | "notify" | "payment" | "access" | "booking";
export const CLAIM_KINDS: readonly ClaimKind[] = ["case", "notify", "payment", "access", "booking"];
/** A system step that was refused or failed with no failure path (CUSTOM `steps_failed`). */
export interface ProblemView {
  node: string | null;
  kind: "denied" | "failed" | "rejected";
  reasons: string[];
  comment: string | null;
}
export interface StepView {
  name: string;
  status: "running" | "done";
  /** The step's label from the agent spec (node `label`), in both languages. */
  label?: { en: string; ar: string } | null;
  /** A platform step described by an i18n key under `chat:` with values (ADR-0067). */
  note?: { key: string; values: Record<string, string | number> };
}
/** What happened to a document attached in the chat (ADR-0067), from CUSTOM `document_read` / `document_refused`. */
export interface DocumentNoteView {
  documentId: string;
  name: string | null;
  allowed: boolean;
  pages: number;
  readPages: number;
  truncated: boolean;
  /** too_sensitive | not_found | unreadable | processing, when not read. */
  code: string | null;
  reasons: string[];
}
export interface ToolCallView {
  id: string;
  tool: string;
  action: string;
  node: string | null;
  args: string;
  status: "running" | "ok" | "failed";
}
export interface ApprovalView {
  taskId: string;
  node: string | null;
  status: "pending" | "decided" | "invalid_signature";
  /** Roles that may decide (from `approval_requested`), when the orchestrator sends them. */
  roles?: string[];
  /** ADR-0102: the level of an approval chain, and the person it is addressed to by name. */
  level?: { level: number; of: number };
  person?: string;
  /** Why a person must approve (policy approval reasons). */
  reasons?: string[];
  /** approve | edit | reject, once decided. */
  decision?: string | null;
  /** User id of the approver (from `approval_decided`). */
  approver?: string | null;
  /** Number of chat messages when the approval was requested (answers after it come later). */
  afterMessages?: number;
  /** approval (a reviewer signs) | human_input (a question for a person) | decision (choose an option). */
  kind?: "approval" | "human_input" | "decision";
  /** The question asked (input and decision tasks). */
  question?: { en: string; ar: string } | null;
  /** "requester" when the person who started the run may answer it. */
  answerBy?: "requester" | null;
  /** Who started the run (user id). */
  requestedBy?: string | null;
  /** JSON Schema of the answer of an input task (null: free text). */
  answerSchema?: Record<string, unknown> | null;
  /** The options of a low-confidence decision (raw `decision` payload of the event). */
  decisionRequest?: Record<string, unknown> | null;
  /** ADR-0097: nobody decided half-way through the time (a reminder went out). */
  reminded?: boolean;
  /** ADR-0097: nobody decided in time; the request went on (how many times). */
  escalated?: number;
}
/** A low-confidence decision a safe test took with the AI's own suggestion (CUSTOM `test_decision_simulated`). */
export interface SimulatedDecisionView {
  node: string | null;
  question: { en: string; ar: string } | null;
  value: string | boolean | number | null;
  valueLabel: { en: string; ar: string } | null;
  probability: number | null;
}
/** A step that reached its repeat limit (CUSTOM `step_limit_reached`); `to` null: the run stopped. */
export interface StepLimitView {
  node: string | null;
  label: { en: string; ar: string } | null;
  limit: number | null;
  to: string | null;
  toLabel: { en: string; ar: string } | null;
}
/** A system step tried again after a failed call (CUSTOM `tool_retry`, ADR-0079). */
export interface ToolRetryView {
  node: string | null;
  attempt: number;
}
/** Guardrails verdicts seen during the run (`safety_check` events, ADR-0054). */
export interface SafetyView {
  input: string | null;
  output: string | null;
}
/** A typed control decision taken during the run (ADR-0059), from CUSTOM `decision_made`. */
export interface DecisionView {
  node: string | null;
  question: { en: string; ar: string } | null;
  kind: string;
  value: string | boolean | number | null;
  valueLabel: { en: string; ar: string } | null;
  probability: number | null;
  calibrated: boolean;
  method: string | null;
  by: "model" | "person";
  outcome: "decided" | "asked_person" | "low_confidence_route";
  taskId: string | null;
  decidedBy: string | null;
}
/** One call of a team member (ADR-0093): its own run, nested (CUSTOM `member_started` … `member_finished`). */
export interface MemberRunView {
  runId: string;
  node: string | null;
  member: string;
  label: { en: string; ar: string } | null;
  kind: "supervisor" | "handoff";
  /** What the supervisor asked it, or why the conversation went to it. */
  request: string | null;
  reason: string | null;
  status: "running" | "completed" | "failed" | "refused" | "cancelled";
  answer: string | null;
  /** Why it was refused or failed, in the platform's words. */
  problem: string | null;
  /** The member run's own events folded the same way (its steps, text, its own members). */
  run: RunViewState;
}
/** One item of a for-each step (ADR-0104): how it went and its own run. */
export interface LoopItemView {
  index: number;
  status: "running" | "done" | "failed" | "skipped" | "not_run";
  runId: string | null;
  error: string | null;
}
/** A for-each step of the run (CUSTOM `foreach_started` … `foreach_item` … `foreach_finished`). */
export interface LoopView {
  node: string;
  label: { en: string; ar: string } | null;
  total: number;
  concurrency: number;
  items: LoopItemView[];
  finished: boolean;
}

/** A "Wait for" step the run is paused at (ADR-0094). */
export interface WaitView {
  waitId: string;
  node: string;
  label: { en: string; ar: string } | null;
  /** What ends it: event, answer, return, time (whichever first). */
  for: string[];
  event: string | null;
  resumeAt: string | null;
  giveUpAt: string | null;
}

export interface RunViewState {
  runId: string | null;
  status: RunStatus;
  messages: ChatMessage[];
  steps: StepView[];
  toolCalls: ToolCallView[];
  approvals: ApprovalView[];
  citations: Citation[];
  safety: SafetyView;
  decisions: DecisionView[];
  /** Low-confidence decisions a safe test took with the AI's suggestion. */
  simulatedDecisions: SimulatedDecisionView[];
  /** Steps that reached their repeat limit. */
  limits: StepLimitView[];
  /** System steps tried again after a failed call (ADR-0079). */
  retries: ToolRetryView[];
  /** Attached documents of this turn: read by the agent or refused, with the reason. */
  documents: DocumentNoteView[];
  /** Earlier turns of the conversation the agent was given (0 for a new conversation). */
  historyTurns: number;
  /** This run replaced one the person corrected (ADR-0084). */
  correction: CorrectionView | null;
  /** System steps that did not work (G-io-09). */
  problems: ProblemView[];
  /** ADR-0100: actions the answer claims that no system call of the run did (CUSTOM `claims_unbacked`). */
  claims?: ClaimKind[];
  /** G-camp-cs-21: what the person said could not be kept in memory, after retries (CUSTOM `memory_not_kept`). */
  memoryNotKept?: boolean;
  /** Team members asked during the run, each with its own run nested (ADR-0093). */
  members: MemberRunView[];
  /** For-each steps of the run with each item's progress (ADR-0104). */
  loops?: LoopView[];
  /** Handoff teams: the member helping now. */
  helping: { member: string; label: { en: string; ar: string } | null; reason: string | null } | null;
  /** The "Wait for" step the run is paused at (ADR-0094), if any. */
  wait?: WaitView | null;
  /** Displayable results of the run's system calls (CUSTOM `view`, ADR-0115): kind, hints and a reference. */
  toolViews?: (ViewBlock & { node?: string | null })[];
  error: string | null;
  /** Individual policy or failure reasons behind `error`, for plain-language explanations. */
  errorReasons: string[];
  lastEventId: string | null;
  eventCount: number;
}

export const initialRunState: RunViewState = {
  runId: null,
  status: "idle",
  messages: [],
  steps: [],
  toolCalls: [],
  approvals: [],
  citations: [],
  safety: { input: null, output: null },
  decisions: [],
  simulatedDecisions: [],
  limits: [],
  retries: [],
  documents: [],
  historyTurns: 0,
  correction: null,
  problems: [],
  members: [],
  helping: null,
  error: null,
  errorReasons: [],
  lastEventId: null,
  eventCount: 0,
};

export type RunAction =
  | { kind: "reset" }
  | { kind: "user_message"; id: string; content: string }
  /** A message sent while the agent was working (ADR-0084): the run is replaced. */
  | { kind: "correction"; id: string; content: string }
  /** The person talked over the answer being read aloud. */
  | { kind: "read_cut_off"; messageId: string }
  | { kind: "started"; runId: string }
  | { kind: "event"; id: string | null; event: AgUiEvent }
  | { kind: "approval_decided"; taskId: string; decision?: string; approver?: string | null }
  /** A rich reply was clicked here (ADR-0088): it is disabled at once. */
  | { kind: "reply_used"; replyId: string; runId?: string | null }
  | { kind: "stream_error"; message: string; reasons?: string[] };

const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
const strs = (v: unknown): string[] | undefined => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : undefined);

function updateLast<T>(items: T[], pred: (t: T) => boolean, fn: (t: T) => T): T[] {
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i] as T;
    if (pred(item)) {
      const copy = items.slice();
      copy[i] = fn(item);
      return copy;
    }
  }
  return items;
}

/** The optional spoken version of an answer (ADR-0070): a short plain string. */
function spokenOf(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() ? v.trim().slice(0, 1000) : undefined;
}

function applyEvent(s: RunViewState, e: AgUiEvent, seq: number): RunViewState {
  switch (e.type) {
    case "RUN_STARTED":
      return { ...s, runId: str(e.runId) ?? s.runId, status: "running", error: null };
    case "RUN_FINISHED": {
      const spoken = spokenOf(e.spoken);
      const lastAssistant = spoken ? s.messages.map((m) => m.role === "assistant" && !m.internal).lastIndexOf(true) : -1;
      return {
        ...s,
        status: "finished",
        messages: s.messages.map((m, i) => ({ ...m, streaming: false, ...(i === lastAssistant && spoken ? { spoken } : {}) })),
        steps: s.steps.map((st) => ({ ...st, status: "done" })),
      };
    }
    case "RUN_ERROR": {
      // AG-UI uses `message`; the orchestrator's workflow reports `reason`.
      const error = str(e.message) ?? str(e.reason) ?? str(e.code) ?? "error";
      const reasons = strs(e.reasons) ?? [str(e.code) === "runaway" && !explainReason(error) ? "runaway" : error];
      return { ...s, status: "error", error, errorReasons: reasons };
    }
    case "RUN_CANCELLED":
      return { ...s, status: "cancelled", error: str(e.reason), errorReasons: str(e.reason) ? [str(e.reason) as string] : [] };
    case "STEP_STARTED": {
      // The orchestrator's steps are graph segments (`segment`): shown as "working" until the
      // segment finishes and names the spec steps it went through.
      const name = str(e.stepName) ?? str(e.node) ?? str(e.step) ?? (typeof e.segment === "number" ? WORKING : `step-${seq}`);
      return { ...s, steps: [...s.steps, { name, status: "running" }] };
    }
    case "STEP_FINISHED": {
      const name = str(e.stepName) ?? str(e.node) ?? str(e.step);
      const trail = strs(e.trail);
      if (name === null && trail) return finishSegment(s, trail, e.labels);
      return {
        ...s,
        steps: updateLast(
          s.steps,
          (st) => st.status === "running" && (name === null || st.name === name),
          (st) => ({ ...st, status: "done" }),
        ),
      };
    }
    case "TEXT_MESSAGE_START": {
      const id = str(e.messageId) ?? `m-${seq}`;
      if (s.messages.some((m) => m.id === id)) return s;
      const files = Array.isArray(e.files)
        ? (e.files as unknown[]).filter((f): f is AnswerFile => !!f && typeof f === "object" && typeof (f as AnswerFile).name === "string" && typeof (f as AnswerFile).text === "string").map((f) => ({ name: f.name, media_type: String(f.media_type ?? "text/plain"), text: f.text }))
        : [];
      const msg: ChatMessage = { id, role: "assistant", content: "", streaming: true, ...(e.format === "markdown" ? { format: "markdown" as const } : {}), ...(files.length ? { files } : {}) };
      if (e.internal === true) msg.internal = true;
      const step = str(e.step);
      if (step) msg.step = step;
      if (e.view_fallback === true) msg.viewFallback = true;
      const fallbackOf = str(e.rich_fallback);
      if (fallbackOf) msg.richFallback = fallbackOf;
      const spoken = spokenOf(e.spoken);
      if (spoken) msg.spoken = spoken;
      const member = str(e.member);
      if (member) {
        msg.member = member;
        msg.memberLabel = bilingual(e.member_label);
      }
      return { ...s, messages: [...s.messages, msg] };
    }
    case "TEXT_MESSAGE_CONTENT": {
      const id = str(e.messageId) ?? `m-${seq}`;
      const delta = typeof e.delta === "string" ? e.delta : "";
      if (!s.messages.some((m) => m.id === id)) {
        return { ...s, messages: [...s.messages, { id, role: "assistant", content: delta, streaming: true }] };
      }
      return { ...s, messages: s.messages.map((m) => (m.id === id ? { ...m, content: m.content + delta } : m)) };
    }
    case "TEXT_MESSAGE_END": {
      const id = str(e.messageId);
      return { ...s, messages: s.messages.map((m) => (m.id === id ? { ...m, streaming: false } : m)) };
    }
    case "TOOL_CALL_START": {
      const tool = str(e.tool) ?? str(e.toolCallName) ?? "tool";
      const id = str(e.toolCallId) ?? `tc-${seq}`;
      const call: ToolCallView = {
        id,
        tool,
        action: str(e.action) ?? "",
        node: str(e.node),
        args: "",
        status: "running",
      };
      return { ...s, toolCalls: [...s.toolCalls, call] };
    }
    case "TOOL_CALL_ARGS": {
      const id = str(e.toolCallId);
      const delta = typeof e.delta === "string" ? e.delta : "";
      return { ...s, toolCalls: s.toolCalls.map((c) => (c.id === id ? { ...c, args: c.args + delta } : c)) };
    }
    case "TOOL_CALL_END": {
      const id = str(e.toolCallId);
      const tool = str(e.tool);
      const node = str(e.node);
      const ok = e.ok !== false && !e.error;
      return {
        ...s,
        toolCalls: updateLast(
          s.toolCalls,
          (c) => c.status === "running" && (id ? c.id === id : (tool === null || c.tool === tool) && (node === null || c.node === node)),
          (c) => ({ ...c, status: ok ? "ok" : "failed" }),
        ),
      };
    }
    case "CUSTOM":
      return applyCustom(s, e);
    default:
      return s;
  }
}

function applyCustom(s: RunViewState, e: AgUiEvent): RunViewState {
  const value = (e.value && typeof e.value === "object" ? e.value : {}) as Record<string, unknown>;
  const field = (k: string): unknown => e[k] ?? value[k];
  switch (e.name) {
    case "approval_requested": {
      const taskId = str(field("task_id"));
      if (!taskId || s.approvals.some((a) => a.taskId === taskId)) return s;
      const roles = strs(field("approver_roles"));
      const reasons = strs(field("reasons"));
      const view: ApprovalView = { taskId, node: str(field("node")), status: "pending", afterMessages: s.messages.length, ...(roles ? { roles } : {}), ...(reasons ? { reasons } : {}) };
      const kind = field("kind");
      if (kind === "approval" || kind === "human_input" || kind === "decision") view.kind = kind;
      const question = bilingual(field("question"));
      if (question) view.question = question;
      if (field("answer_by") === "requester") view.answerBy = "requester";
      const requestedBy = str(field("requested_by"));
      if (requestedBy) view.requestedBy = requestedBy;
      const schema = field("answer_schema");
      if (schema && typeof schema === "object" && !Array.isArray(schema)) view.answerSchema = schema as Record<string, unknown>;
      const decision = field("decision");
      if (decision && typeof decision === "object" && !Array.isArray(decision)) view.decisionRequest = decision as Record<string, unknown>;
      // ADR-0102: a level of an approval chain, maybe addressed to a person by name.
      const chain = field("chain");
      if (chain && typeof chain === "object" && !Array.isArray(chain)) {
        const c = chain as Record<string, unknown>;
        if (typeof c.level === "number" && typeof c.of === "number") view.level = { level: c.level, of: c.of };
        const person = str(c.person_name) ?? str(c.person);
        if (person) view.person = person;
      }
      return { ...s, status: "awaiting_approval", approvals: [...s.approvals, view] };
    }
    case "waiting": {
      const view: WaitView = {
        waitId: str(field("wait_id")) ?? "",
        node: str(field("node")) ?? "",
        label: bilingual(field("label")),
        for: strs(field("for")) ?? [],
        event: str(field("event")),
        resumeAt: str(field("resume_at")),
        giveUpAt: str(field("give_up_at")),
      };
      return { ...s, wait: view };
    }
    case "wait_ended": {
      const taskId = str(field("task_id"));
      // The wait's question (if nobody answered it) was withdrawn with it.
      const approvals = taskId ? s.approvals.map((a) => (a.taskId === taskId && a.status !== "decided" ? { ...a, status: "decided" as const } : a)) : s.approvals;
      return { ...s, wait: null, approvals, status: s.status === "awaiting_approval" ? "running" : s.status };
    }
    case "approval_reminded": {
      const taskId = str(field("task_id"));
      return { ...s, approvals: s.approvals.map((a) => (a.taskId === taskId ? { ...a, reminded: true } : a)) };
    }
    case "approval_escalated": {
      const taskId = str(field("task_id"));
      const roles = strs(field("approver_roles"));
      return { ...s, approvals: s.approvals.map((a) => (a.taskId === taskId ? { ...a, reminded: false, escalated: (a.escalated ?? 0) + 1, ...(roles ? { roles } : {}) } : a)) };
    }
    case "approval_rejected_invalid_signature": {
      const taskId = str(field("task_id"));
      return {
        ...s,
        approvals: s.approvals.map((a) => (a.taskId === taskId ? { ...a, status: "invalid_signature" } : a)),
      };
    }
    case "approval_decided":
      return decided(s, str(field("task_id")), str(field("decision")), str(field("approver")));
    case "decision_made":
      return { ...s, decisions: [...s.decisions, decisionView(e)] };
    case "test_decision_simulated": {
      const d = decisionView(e);
      return { ...s, simulatedDecisions: [...s.simulatedDecisions, { node: d.node, question: d.question, value: d.value, valueLabel: d.valueLabel, probability: d.probability }] };
    }
    case "step_limit_reached": {
      const limit = Number(field("limit"));
      const view: StepLimitView = { node: str(field("node")), label: bilingual(field("label")), limit: Number.isFinite(limit) ? limit : null, to: str(field("to")), toLabel: bilingual(field("to_label")) };
      return { ...s, limits: [...s.limits, view] };
    }
    case "tool_retry": {
      const attempt = Number(field("attempt"));
      return { ...s, retries: [...s.retries, { node: str(field("node")), attempt: Number.isFinite(attempt) ? attempt : 1 }] };
    }
    case "safety_check": {
      const direction = str(field("direction")) === "output" ? "output" : "input";
      return { ...s, safety: { ...s.safety, [direction]: str(field("decision")) ?? "allow" } };
    }
    case "document_read":
    case "document_refused": {
      const doc = documentView(field);
      if (!doc) return s;
      const documents = [...s.documents.filter((d) => d.documentId !== doc.documentId), doc];
      if (!doc.allowed) return { ...s, documents };
      const note: NonNullable<StepView["note"]> = doc.truncated
        ? { key: "activity.readDocumentPartly", values: { read: doc.readPages, pages: doc.pages } }
        : { key: "activity.readDocument", values: { count: doc.pages } };
      return { ...s, documents, steps: [...s.steps, { name: `read_${doc.documentId}`, status: "done", note }] };
    }
    case "conversation_context": {
      const turns = Number(field("turns"));
      if (!Number.isFinite(turns) || turns <= 0) return s;
      return { ...s, historyTurns: turns, steps: [...s.steps, { name: "conversation", status: "done", note: { key: "activity.usedConversation", values: { count: turns } } }] };
    }
    case "correction_applied": {
      const steps = field("steps");
      const list = (v: unknown): string[] => strs(v) ?? [];
      const byLang = steps && typeof steps === "object" && !Array.isArray(steps) ? (steps as Record<string, unknown>) : {};
      const changed = Array.isArray(field("changed")) ? (field("changed") as unknown[]) : [];
      return {
        ...s,
        correction: {
          status: str(field("status")) ?? "unknown",
          steps: { en: list(byLang.en), ar: list(byLang.ar) },
          changed: changed.filter((c): c is Record<string, unknown> => !!c && typeof c === "object").map((c) => ({ node: str(c.node), label: bilingual(c.label) })),
        },
      };
    }
    case "steps_failed": {
      const raw = Array.isArray(field("problems")) ? (field("problems") as unknown[]) : [];
      const problems: ProblemView[] = raw
        .filter((p): p is Record<string, unknown> => !!p && typeof p === "object")
        .map((p) => ({ node: str(p.node), kind: p.kind === "denied" || p.kind === "rejected" ? p.kind : "failed", reasons: strs(p.reasons) ?? [], comment: str(p.comment) }));
      return { ...s, problems: [...s.problems, ...problems] };
    }
    case "claims_unbacked": {
      const raw = Array.isArray(field("claims")) ? (field("claims") as unknown[]) : [];
      const kinds = raw.filter((k): k is ClaimKind => typeof k === "string" && (CLAIM_KINDS as readonly string[]).includes(k));
      return kinds.length ? { ...s, claims: [...new Set([...(s.claims ?? []), ...kinds])] } : s;
    }
    case "memory_not_kept":
      return { ...s, memoryNotKept: true };
    case "foreach_started": {
      const node = str(field("node"));
      if (!node) return s;
      const loop: LoopView = { node, label: bilingual(field("label")), total: Number(field("total") ?? 0), concurrency: Number(field("concurrency") ?? 1), items: [], finished: false };
      return { ...s, loops: [...(s.loops ?? []).filter((l) => l.node !== node || l.finished), loop] };
    }
    case "foreach_item": {
      const node = str(field("node"));
      const index = Number(field("index"));
      if (!node || !Number.isInteger(index)) return s;
      const raw = str(field("status"));
      const status: LoopItemView["status"] = raw === "done" || raw === "failed" || raw === "skipped" || raw === "not_run" ? raw : "running";
      const item: LoopItemView = { index, status, runId: str(field("run_id")), error: str(field("error")) };
      const loops = (s.loops ?? []).map((l) => (l.node === node && !l.finished ? { ...l, items: [...l.items.filter((x) => x.index !== index), item].sort((a, b) => a.index - b.index) } : l));
      return { ...s, loops };
    }
    case "foreach_finished": {
      const node = str(field("node"));
      return { ...s, loops: (s.loops ?? []).map((l) => (l.node === node && !l.finished ? { ...l, finished: true } : l)) };
    }
    case "member_started": {
      const runId = str(field("run_id"));
      const member = str(field("member"));
      if (!runId || !member || s.members.some((x) => x.runId === runId)) return s;
      const view: MemberRunView = {
        runId,
        node: str(field("node")),
        member,
        label: bilingual(field("label")),
        kind: field("kind") === "handoff" ? "handoff" : "supervisor",
        request: str(field("request")),
        reason: str(field("reason")),
        status: "running",
        answer: null,
        problem: null,
        run: { ...initialRunState, runId, status: "running" },
      };
      return { ...s, members: [...s.members, view] };
    }
    case "member_finished": {
      const runId = str(field("run_id"));
      const raw = str(field("status"));
      const status: MemberRunView["status"] = raw === "completed" || raw === "refused" || raw === "cancelled" ? raw : "failed";
      const finish = (x: MemberRunView): MemberRunView => ({ ...x, status, answer: str(field("answer")) ?? x.answer, problem: str(field("reason")) ?? x.problem });
      if (runId && s.members.some((x) => x.runId === runId)) return { ...s, members: s.members.map((x) => (x.runId === runId ? finish(x) : x)) };
      // Refused before it started: no run of its own, still shown.
      const member = str(field("member"));
      if (!member) return s;
      const view: MemberRunView = { runId: runId ?? `${member}-${s.members.length + 1}`, node: str(field("node")), member, label: bilingual(field("label")), kind: field("kind") === "handoff" ? "handoff" : "supervisor", request: str(field("request")), reason: null, status: "running", answer: null, problem: null, run: initialRunState };
      return { ...s, members: [...s.members, finish(view)] };
    }
    case "member_event": {
      const runId = str(field("run_id"));
      const inner = field("event");
      if (!runId || !inner || typeof inner !== "object") return s;
      return withMemberEvent(s, runId, inner as AgUiEvent);
    }
    case "now_helping": {
      const member = str(field("member"));
      if (!member) return s;
      const label = bilingual(field("label"));
      const reason = str(field("reason"));
      const note: ChatMessage = { id: `helping-${s.eventCount}-${member}`, role: "assistant", content: "", streaming: false, notice: "helping", member, memberLabel: label, helpingReason: reason };
      return { ...s, helping: { member, label, reason }, messages: [...s.messages, note] };
    }
    case "rich_reply":
      return withReply(s, richReplyOf(e));
    case "view": {
      // A system call returned something displayable: kept for lookups and record cards; an
      // answer says which ones it shows (`answer_views`). A block sent for one message is shown.
      const block = viewBlockOf(e.value ?? e);
      if (!block) return s;
      if (str(field("message_id")) || (e.value && typeof e.value === "object" && (e.value as Record<string, unknown>).type === "view")) return withView(s, block, str(field("message_id")));
      return { ...s, toolViews: [...(s.toolViews ?? []), { ...block, node: str(field("node")) }] };
    }
    case "answer_views": {
      const views = (Array.isArray(field("views")) ? (field("views") as unknown[]) : []).map(viewBlockOf).filter((b): b is ViewBlock => !!b);
      let next = s;
      const node = str(field("node"));
      const target = node ? [...s.messages].reverse().find((m) => m.role === "assistant" && m.step === node && !m.viewFallback && !m.internal) : undefined;
      for (const v of views) next = withView(next, v, target?.id ?? null);
      return next;
    }
    case "rich_reply_used":
    case "rich_reply_expired":
      return replyUsed(s, str(field("reply_id")), str(e.runId) ?? s.runId);
    case "retrieval_results": {
      const results = (field("results") ?? []) as Citation[];
      if (!Array.isArray(results)) return s;
      const seen = new Set(s.citations.map((c) => c.id));
      return { ...s, citations: [...s.citations, ...results.filter((c) => c && !seen.has(c.id))] };
    }
    default:
      return s;
  }
}

/** Folds a member run's own event into its nested view; a member's member is found by its id prefix. */
function withMemberEvent(s: RunViewState, runId: string, event: AgUiEvent): RunViewState {
  const at = s.members.findIndex((x) => x.runId === runId || runId.startsWith(`${x.runId}--sub-`));
  if (at < 0) return s;
  const target = s.members[at] as MemberRunView;
  const run = target.runId === runId ? runReducer(target.run, { kind: "event", id: null, event }) : withMemberEvent(target.run, runId, event);
  const members = s.members.slice();
  members[at] = { ...target, run };
  return { ...s, members };
}

/** Attaches a rich reply to the plain-text message it replaces (or adds one when that is missing). */
function withReply(s: RunViewState, found: RichReply | null): RunViewState {
  if (!found) return s;
  const reply = found.run_id ? found : { ...found, run_id: s.runId };
  // Reply ids are unique within a run: match the message by id, else this run's plain text.
  const at = reply.message_id ? s.messages.findIndex((m) => m.id === reply.message_id) : s.messages.findIndex((m) => m.richFallback === reply.reply_id && !m.rich);
  if (at < 0) return { ...s, messages: [...s.messages, { id: reply.message_id || `reply-${reply.reply_id}`, role: "assistant", content: reply.fallback[reply.lang] || reply.fallback.en, streaming: false, richFallback: reply.reply_id, rich: reply }] };
  return { ...s, messages: s.messages.map((m, i) => (i === at ? { ...m, rich: reply } : m)) };
}

/**
 * Attaches a result display (ADR-0117) to its message (by id), else to the answer of this turn
 * being written, else as its own message after the person's last question.
 */
function withView(s: RunViewState, block: ViewBlock | null, messageId: string | null): RunViewState {
  if (!block) return s;
  const lastUser = s.messages.map((m) => m.role).lastIndexOf("user");
  let at = messageId ? s.messages.findIndex((m) => m.id === messageId) : -1;
  if (at < 0 && !messageId) {
    for (let i = s.messages.length - 1; i > lastUser; i--) {
      const m = s.messages[i] as ChatMessage;
      if (m.role === "assistant" && !m.internal && !m.notice && !m.viewFallback) {
        at = i;
        break;
      }
    }
  }
  if (at < 0) return { ...s, messages: [...s.messages, { id: messageId ?? `view-${s.eventCount}`, role: "assistant", content: "", streaming: false, views: [block] }] };
  return { ...s, messages: s.messages.map((m, i) => (i === at ? { ...m, views: [...(m.views ?? []), block] } : m)) };
}

function replyUsed(s: RunViewState, replyId: string | null, runId: string | null = s.runId): RunViewState {
  if (!replyId) return s;
  const hit = (m: ChatMessage) => m.rich?.reply_id === replyId && (!runId || !m.rich.run_id || m.rich.run_id === runId);
  return { ...s, messages: s.messages.map((m) => (m.rich && hit(m) ? { ...m, rich: { ...m.rich, used: true } } : m)) };
}

/** The rich reply the run waits on (a button that runs a step), while it is not answered. */
export function waitingReply(s: Pick<RunViewState, "status" | "messages">): RichReply | undefined {
  if (s.status !== "running") return undefined;
  return s.messages.map((m) => m.rich).find((r): r is RichReply => !!r && r.waiting && !r.used);
}

/** Placeholder name of a running graph segment (replaced by its steps when it finishes). */
export const WORKING = "working";

function finishSegment(s: RunViewState, trail: string[], labels: unknown): RunViewState {
  const named = labels && typeof labels === "object" ? (labels as Record<string, unknown>) : {};
  const at = s.steps.map((st) => st.status === "running" && st.name === WORKING).lastIndexOf(true);
  const done: StepView[] = trail.map((node) => ({ name: node, status: "done", label: bilingual(named[node]) }));
  if (at < 0) return { ...s, steps: [...s.steps, ...done] };
  return { ...s, steps: [...s.steps.slice(0, at), ...done, ...s.steps.slice(at + 1)] };
}

function documentView(field: (k: string) => unknown): DocumentNoteView | null {
  const id = str(field("document_id"));
  if (!id) return null;
  const num = (k: string): number => {
    const v = Number(field(k));
    return Number.isFinite(v) && v > 0 ? Math.floor(v) : 0;
  };
  return {
    documentId: id,
    name: str(field("document_name")),
    allowed: field("allowed") === true,
    pages: num("pages"),
    readPages: num("read_pages"),
    truncated: field("truncated") === true,
    code: str(field("code")),
    reasons: strs(field("reasons")) ?? [],
  };
}

const bilingual = (v: unknown): { en: string; ar: string } | null =>
  v && typeof v === "object" && typeof (v as { en?: unknown }).en === "string" ? { en: (v as { en: string }).en, ar: String((v as { ar?: unknown }).ar ?? (v as { en: string }).en) } : null;

function decisionView(e: AgUiEvent): DecisionView {
  // The decision's own `value` collides with the AG-UI CUSTOM `value` envelope: when the
  // envelope is an object the fields live inside it, otherwise they are top-level.
  const inner = e.value && typeof e.value === "object" && !Array.isArray(e.value) ? (e.value as Record<string, unknown>) : null;
  const field = (k: string): unknown => (inner ? inner[k] : e[k]);
  const value = field("value");
  const p = field("probability");
  const outcome = field("outcome");
  return {
    node: str(field("node")),
    question: bilingual(field("question")),
    kind: str(field("kind")) ?? "choice",
    value: typeof value === "string" || typeof value === "boolean" || typeof value === "number" ? value : null,
    valueLabel: bilingual(field("value_label")),
    probability: typeof p === "number" && Number.isFinite(p) ? Math.min(1, Math.max(0, p)) : null,
    calibrated: field("calibrated") === true,
    method: str(field("method")),
    by: field("by") === "person" ? "person" : "model",
    outcome: outcome === "asked_person" || outcome === "low_confidence_route" ? outcome : "decided",
    taskId: str(field("task_id")),
    decidedBy: str(field("decided_by")),
  };
}

function decided(s: RunViewState, taskId: string | null, decision?: string | null, approver?: string | null): RunViewState {
  const approvals = s.approvals.map((a) =>
    a.taskId === taskId
      ? { ...a, status: "decided" as const, decision: decision ?? a.decision ?? null, approver: approver ?? a.approver ?? null }
      : a,
  );
  const stillPending = approvals.some((a) => a.status === "pending");
  return {
    ...s,
    approvals,
    status: s.status === "awaiting_approval" && !stillPending ? "running" : s.status,
  };
}

export function runReducer(s: RunViewState, a: RunAction): RunViewState {
  switch (a.kind) {
    case "reset":
      return initialRunState;
    case "user_message":
      return {
        ...initialRunState,
        messages: [...s.messages.map((m) => ({ ...m, streaming: false })), { id: a.id, role: "user", content: a.content, streaming: false }],
      };
    case "correction": {
      // The answer being written is stale: it stops where it was, marked as cut off.
      const kept = s.messages.map((m) => (m.streaming && m.role === "assistant" ? { ...m, streaming: false, cutOff: true } : { ...m, streaming: false }));
      return {
        ...initialRunState,
        messages: [...kept, { id: a.id, role: "user", content: a.content, streaming: false }, { id: `${a.id}-ack`, role: "assistant", content: "", streaming: false, notice: "correction" }],
      };
    }
    case "read_cut_off":
      return { ...s, messages: s.messages.map((m) => (m.id === a.messageId ? { ...m, readCutOff: true } : m)) };
    case "started":
      return { ...s, runId: a.runId, status: "running" };
    case "event": {
      const next = applyEvent(s, a.event, s.eventCount + 1);
      return { ...next, lastEventId: a.id ?? s.lastEventId, eventCount: s.eventCount + 1 };
    }
    case "approval_decided":
      return decided(s, a.taskId, a.decision, a.approver);
    case "reply_used":
      return replyUsed(s, a.replyId, a.runId ?? null);
    case "stream_error":
      return { ...s, status: "error", error: a.message, errorReasons: a.reasons ?? [a.message] };
  }
}

/** Replays a recorded event log through the reducer (builder trace replay). */
export function replay(events: AgUiEvent[], upTo = events.length): RunViewState {
  let s = initialRunState;
  events.slice(0, upTo).forEach((event, i) => {
    s = runReducer(s, { kind: "event", id: String(i + 1), event });
  });
  return s;
}

/** An assistant message that is the agent's answer to the person (not a step's working text, not empty). */
export function isShownMessage(m: ChatMessage): boolean {
  if (m.role === "user" || m.notice) return true;
  if (m.internal || m.viewFallback) return false;
  return m.streaming || m.content.trim() !== "" || !!m.views?.length;
}

/** The conversation as the person sees it: working text of intermediate steps and empty replies left out. */
export function visibleMessages(s: Pick<RunViewState, "messages">): ChatMessage[] {
  return s.messages.filter(isShownMessage);
}

/**
 * Where each approval or question goes in the conversation (G-camp-cs-12): before the first message
 * that came after it was asked, so the answer written after an approval reads below it instead of
 * hidden above the approval cards. `after`: those asked after the last message.
 */
export function placeApprovals(s: Pick<RunViewState, "messages" | "approvals">): { before: Map<string, ApprovalView[]>; after: ApprovalView[] } {
  const before = new Map<string, ApprovalView[]>();
  const after: ApprovalView[] = [];
  for (const a of s.approvals) {
    const at = a.afterMessages ?? s.messages.length;
    const next = s.messages.slice(at).find(isShownMessage);
    if (next) before.set(next.id, [...(before.get(next.id) ?? []), a]);
    else after.push(a);
  }
  return { before, after };
}

/** Text that intermediate steps wrote (a classifier's code, drafts): for the activity panel only. */
export function workingNotes(s: Pick<RunViewState, "messages">): ChatMessage[] {
  return s.messages.filter((m) => m.role === "assistant" && m.internal && m.content.trim() !== "");
}

/** The first task still waiting for a person. */
export function pendingTask(s: Pick<RunViewState, "approvals">): ApprovalView | undefined {
  return s.approvals.find((a) => a.status === "pending");
}

/** True while the run waits for a person's answer (a question or a choice), not an approval. */
export function waitingForAnswer(s: Pick<RunViewState, "status" | "approvals">): boolean {
  const p = pendingTask(s);
  return s.status === "awaiting_approval" && !!p && (p.kind === "human_input" || p.kind === "decision");
}

/** The status key for labels: `awaiting_input` while a person's answer (or click) is awaited. */
export function statusKey(s: Pick<RunViewState, "status" | "approvals"> & Partial<Pick<RunViewState, "messages">>): RunStatus | "awaiting_input" {
  if (s.messages && waitingReply({ status: s.status, messages: s.messages })) return "awaiting_input";
  return waitingForAnswer(s) ? "awaiting_input" : s.status;
}
